// Confirmation dialog for message deletion.
//
// Portal-rendered through `usePortalRoot()` so the overlay renders
// inside the embed's bounding box when wrapped in `<EmbeddableChat>`,
// and against `document.body` for the standalone shell — stacking
// contexts inside the message row (grids, transforms) can't clip
// either surface. Focus-trapped inside the dialog on open; Escape +
// outside-click close it (while idle); focus restores to the element
// that had focus when the dialog opened. Errors from `onConfirm`
// surface in the dialog body as a `role="status"` region so AT users
// hear the failure.

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import { DangerButton, GhostButton } from "~/components/MessageButtons";
import { isEmbeddedPortal } from "~/lib/portal-positioning";

interface DeleteConfirmDialogProps {
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function DeleteConfirmDialog({ onConfirm, onCancel }: DeleteConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const portalRoot = usePortalRoot();

  // Only allow user-initiated close while idle. An in-flight redaction
  // that's about to resolve shouldn't have the overlay snatched out
  // from under it; the parent will unmount the dialog on success.
  const requestCancel = useCallback(() => {
    if (busy) return;
    onCancel();
  }, [busy, onCancel]);

  // Remember the focused element on mount so we can restore it on
  // close. Without this the user lands back on the page body and
  // loses their place in the timeline.
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    cancelRef.current?.focus();
    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
  }, []);

  // Escape closes; Tab / Shift+Tab cycle focus within the dialog so
  // AT users can't fall back into the timeline.
  const trapFocus = useCallback((e: KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const node = dialogRef.current;
    if (!node) return;
    const focusable = node.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        requestCancel();
        return;
      }
      trapFocus(e);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [requestCancel, trapFocus]);

  // The scrim is purely presentational. `role="dialog"` + `aria-modal`
  // live on the inner panel so screen readers announce the dialog
  // boundary correctly. Embed-rooted portals position the scrim
  // absolutely (the embed root is already `position: relative`);
  // body-rooted portals stay viewport-fixed.
  const scrimPosition: "fixed" | "absolute" = isEmbeddedPortal(portalRoot) ? "absolute" : "fixed";
  const dialog = (
    <div
      style={{
        position: scrimPosition,
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--chat-scrim-dialog)",
      }}
      onClick={requestCancel}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-title"
        aria-describedby={error ? "delete-status" : "delete-body"}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(var(--chat-dialog-confirm-width), calc(100% - 2 * var(--chat-space-4)))",
          boxSizing: "border-box",
          minHeight: "var(--chat-dialog-confirm-min-height)",
          padding: "var(--chat-space-4)",
          borderRadius: "var(--chat-radius-xl)",
          background: "var(--chat-surface-dialog)",
          boxShadow: "var(--chat-shadow-dialog)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-space-3)",
        }}
      >
        <h2
          id="delete-title"
          style={{
            margin: 0,
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-display)",
            fontSize: "var(--chat-type-display-sm-size)",
            lineHeight: "var(--chat-type-display-sm-line)",
            fontWeight: 500,
          }}
        >
          Delete message?
        </h2>
        <p
          id="delete-body"
          style={{
            margin: 0,
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
          }}
        >
          This can&apos;t be undone. The message will be removed for everyone in this room.
        </p>
        <p
          id="delete-status"
          role="status"
          aria-live="polite"
          style={{
            margin: 0,
            minHeight: "var(--chat-type-body-sm-line)",
            color: "var(--chat-state-danger)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
          }}
        >
          {error}
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "var(--chat-space-2)",
            marginTop: "auto",
          }}
        >
          <GhostButton ref={cancelRef} onClick={requestCancel} disabled={busy}>
            Cancel
          </GhostButton>
          <DangerButton
            onClick={async () => {
              if (busy) return;
              setBusy(true);
              setError(null);
              try {
                await onConfirm();
                // Parent unmounts us on success — nothing else to do.
              } catch (err) {
                setError(err instanceof Error ? err.message : "Couldn't delete the message.");
              } finally {
                // Always reset `busy` so Cancel / Delete are usable
                // again on the failure path. If the component has
                // already unmounted (success path) React discards the
                // update; the warning was a React 18 concern, not an
                // error in 19.
                setBusy(false);
              }
            }}
            disabled={busy}
          >
            Delete
          </DangerButton>
        </div>
      </div>
    </div>
  );

  if (!portalRoot) return null;
  return createPortal(dialog, portalRoot);
}
